/**
 * Dose Calculation Utilities
 *
 * Frontend-side dose calculation for supplement recommendations.
 * Weight (0.0-1.0) scales linearly within the mode's dose range.
 *
 * Support Mode:    dose = min_daily_dose + (max_support_daily_dose - min_daily_dose) × weight
 * Therapeutic Mode: dose = min_daily_dose + (max_therapeutic_daily_dose - min_daily_dose) × weight
 */

/**
 * Calculate dose based on product details, recommendation weight, and dosing mode.
 *
 * @param {Object} product_details - Product details from backend
 * @param {number} weight - Recommendation weight (0.0 - 1.0)
 * @param {'support'|'therapeutic'} mode - Dosing mode
 * @returns {Object|null} Dose result with dose, frequency, dailyTotal
 */
export function calculateDose(product_details, weight, mode) {
    if (!product_details) return null;

    const clampedWeight = Math.max(0, Math.min(1, weight));
    const minDaily = product_details.min_daily_dose;

    // Guard: if min_daily_dose is missing or not a number, we can't calculate
    if (minDaily == null || !Number.isFinite(minDaily)) return null;

    const supportMax = product_details.max_support_daily_dose || minDaily;
    const therapeuticMax = product_details.max_therapeutic_daily_dose || supportMax;

    // Calculate dose based on mode - weight scales within the mode's range
    const supportDose = Math.max(minDaily, Math.round(minDaily + (supportMax - minDaily) * clampedWeight));
    const therapeuticDose = Math.max(minDaily, Math.round(minDaily + (therapeuticMax - minDaily) * clampedWeight));

    const targetDaily = mode === 'therapeutic' ? therapeuticDose : supportDose;

    // Optimize frequency
    const maxSingle = product_details.max_single_dose || targetDaily;
    if (targetDaily <= maxSingle) {
        return { dose: targetDaily, frequency: 1, dailyTotal: targetDaily };
    }

    const frequency = Math.ceil(targetDaily / maxSingle);
    return { dose: maxSingle, frequency, dailyTotal: targetDaily };
}

/**
 * Format frequency as human-readable string.
 *
 * @param {number} frequency - Number of times per freq_unit
 * @param {string} freqUnit - 'day' or 'week'
 * @returns {string} Human-readable frequency label
 */
export function formatFrequency(frequency, freqUnit = 'day') {
    if (freqUnit === 'day') {
        if (frequency === 1) return 'once daily';
        if (frequency === 2) return 'twice daily';
        return `${frequency}x daily`;
    }

    if (freqUnit === 'week') {
        if (frequency === 1) return 'once weekly';
        if (frequency === 2) return 'twice weekly';
        return `${frequency}x weekly`;
    }

    return `${frequency}x per ${freqUnit}`;
}

/**
 * Format a snake_case category name to Title Case.
 *
 * @param {string} cat - Category string (e.g. 'digestive_support')
 * @returns {string} Formatted string (e.g. 'Digestive Support')
 */
export function formatCategoryName(cat) {
    if (!cat) return 'Uncategorized';
    return cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Get dose difference between therapeutic and support modes.
 *
 * @param {Object} product_details - Product details
 * @param {number} weight - Recommendation weight
 * @returns {Object} Difference info
 */
export function getDoseDifference(product_details, weight) {
    const support = calculateDose(product_details, weight, 'support');
    const therapeutic = calculateDose(product_details, weight, 'therapeutic');

    if (!support || !therapeutic) return null;

    const difference = therapeutic.dailyTotal - support.dailyTotal;
    const percentageChange = support.dailyTotal > 0
        ? Math.round((difference / support.dailyTotal) * 100)
        : 0;

    return {
        supportDailyTotal: support.dailyTotal,
        therapeuticDailyTotal: therapeutic.dailyTotal,
        difference,
        percentageChange
    };
}
